/**
 * Bundled Help Center content (FR-HELP-03, FR-HELP-05).
 *
 * FAQs are served by the help service (`GET /help/v1/faqs`). The entries in
 * {@link FALLBACK_FAQS} ship with the bundle and are shown when that call
 * fails, so the Help Center still answers the obvious questions at kickoff when
 * the sideline connection is bad.
 *
 * Docs are the Markdown files in `web/src/docs`, imported by Vite at build time
 * (`?raw`) and parsed ONCE here at module load — that parse is the "client
 * cache": every later render, ToC filter and search runs over the in-memory
 * model with no network and no re-parse, which is what keeps a doc render
 * inside the 500 ms p95 budget.
 *
 * Frontmatter contract (see `web/src/docs/play-entry.md`):
 *
 *     ---
 *     title: Play Entry Guide
 *     category: Core Features
 *     persona_tags: [coach, primary_stat_operator]
 *     ---
 *
 * `persona_tags` empty or absent = visible to every persona.
 */
import type { SupportCategory } from './api-types';
import type { Persona } from './personas';

export { FALLBACK_FAQS } from './fallback-faqs';

/**
 * Contact Support categories (FR-HELP-06).
 *
 * `value` is the wire enum the support service validates against; `label` is
 * what a coach reads. They are paired here so the two can never drift — an
 * earlier draft shipped labels only, which the API rejects with 422.
 */
export const SUPPORT_CATEGORY_OPTIONS: readonly {
  value: SupportCategory;
  label: string;
  hint: string;
}[] = [
  { value: 'bug', label: 'Something is broken', hint: 'A page, stat or sync is not working.' },
  { value: 'question', label: 'Question / how do I…', hint: 'Using a feature or finding data.' },
  { value: 'billing', label: 'Billing or subscription', hint: 'Plans, invoices, seats, trials.' },
  { value: 'feature_request', label: 'Feature request', hint: 'Something you wish it did.' },
  { value: 'other', label: 'Account, access or other', hint: 'Anything not covered above.' },
];

/** Labels only — kept for legacy call sites that render a plain list. */
export const SUPPORT_CATEGORIES = SUPPORT_CATEGORY_OPTIONS.map((option) => option.label);

export function supportCategoryLabel(value: SupportCategory): string {
  return SUPPORT_CATEGORY_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

/** Doc pages bundled with the app; `?raw` keeps them as Markdown source. */
const DOC_SOURCES = import.meta.glob('../docs/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

export interface DocSection {
  /** Anchor id — the deep-link target in `/app/docs/{slug}#{id}`. */
  id: string;
  title: string;
  /** Markdown heading level, 1–6. */
  level: number;
}

export interface DocPage {
  /** URL-safe slug derived from the filename. */
  slug: string;
  title: string;
  category: string;
  /** Empty = visible to every persona. */
  personaTags: string[];
  /** Markdown body, frontmatter stripped. */
  source: string;
  sections: DocSection[];
}

export interface DocSearchHit {
  slug: string;
  title: string;
  sectionId: string | null;
  sectionTitle: string | null;
  /** ±80 characters of context around the first match. */
  excerpt: string;
}

const DEFAULT_CATEGORY = 'Reference';
const FRONTMATTER = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/;
const HEADING = /^(#{1,6})[ \t]+(.+?)[ \t]*$/gm;

/** Stable heading → anchor id. Shared with `MarkdownView` so links resolve. */
export function slugifyHeading(title: string): string {
  return title
    .toLowerCase()
    .replace(/`|\*|_/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function unquote(value: string): string {
  return value.trim().replace(/^["']|["']$/g, '');
}

function splitList(value: string): string[] {
  return value
    .replace(/^\[|\]$/g, '')
    .split(',')
    .map(unquote)
    .filter((part) => part !== '');
}

/**
 * Minimal YAML frontmatter reader — scalars and string lists only.
 *
 * It accepts all three shapes Prettier and hand-editing produce, because the
 * docs are formatted by the repo's Prettier run:
 *
 *     persona_tags: [coach, fanatic]      # inline
 *     persona_tags:                       # inline, wrapped over lines
 *       [coach,
 *        fanatic]
 *     persona_tags:                       # block sequence
 *       - coach
 */
function parseFrontmatter(raw: string): { data: Record<string, string[] | string>; body: string } {
  const match = FRONTMATTER.exec(raw);
  if (match === null) return { data: {}, body: raw };

  const data: Record<string, string[] | string> = {};
  const lines = (match[1] ?? '').split(/\r?\n/);

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index] ?? '';
    const separator = line.indexOf(':');
    if (separator === -1 || line.startsWith(' ') || line.startsWith('-')) continue;
    const key = line.slice(0, separator).trim();
    if (key === '') continue;
    let value = line.slice(separator + 1).trim();

    // Inline list that Prettier wrapped: consume lines until the closing "]".
    if ((value === '' || value.startsWith('[')) && !value.includes(']')) {
      let lookahead = index + 1;
      let buffer = value;
      while (lookahead < lines.length && (buffer === '' || !buffer.includes(']'))) {
        const next = (lines[lookahead] ?? '').trim();
        if (buffer === '' && !next.startsWith('[')) break;
        buffer += next;
        lookahead += 1;
      }
      if (buffer.startsWith('[')) {
        data[key] = splitList(buffer);
        index = lookahead - 1;
        continue;
      }
    }

    // Block sequence: `key:` followed by indented `- item` lines.
    if (value === '') {
      const items: string[] = [];
      let lookahead = index + 1;
      while (/^\s*-\s+/.test(lines[lookahead] ?? '')) {
        items.push(unquote((lines[lookahead] ?? '').replace(/^\s*-\s+/, '')));
        lookahead += 1;
      }
      if (items.length > 0) {
        data[key] = items;
        index = lookahead - 1;
        continue;
      }
    }

    if (value.startsWith('[') && value.includes(']')) {
      data[key] = splitList(value);
      continue;
    }
    value = unquote(value);
    if (value !== '') data[key] = value;
  }
  return { data, body: raw.slice(match[0].length) };
}

/**
 * Headings in document order with stable, deduplicated anchor ids.
 *
 * `MarkdownView` consumes this same list while rendering, so the id it puts on
 * an `<h2>` is byte-identical to the one the ToC links to — that is what makes
 * `/app/docs/play-entry#conflict-resolution` land on the right heading.
 */
export function extractSections(body: string): DocSection[] {
  const sections: DocSection[] = [];
  const seen = new Set<string>();
  for (const match of body.matchAll(HEADING)) {
    const level = (match[1] ?? '#').length;
    const title = (match[2] ?? '').trim();
    if (title === '') continue;
    let id = slugifyHeading(title);
    // Duplicate headings still need unique anchors.
    let suffix = 2;
    while (seen.has(id)) id = `${slugifyHeading(title)}-${suffix++}`;
    seen.add(id);
    sections.push({ id, title, level });
  }
  return sections;
}

function toTitle(slug: string): string {
  const spaced = slug.replace(/-/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function asString(value: string[] | string | undefined, fallback: string): string {
  if (Array.isArray(value)) return value[0] ?? fallback;
  return value === undefined || value === '' ? fallback : value;
}

function asList(value: string[] | string | undefined): string[] {
  if (Array.isArray(value)) return value;
  if (value === undefined || value.trim() === '') return [];
  return value
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '');
}

export const DOC_PAGES: readonly DocPage[] = Object.entries(DOC_SOURCES)
  .map(([path, raw]) => {
    const slug = path.split('/').pop()?.replace(/\.md$/, '') ?? path;
    const { data, body } = parseFrontmatter(raw);
    const firstHeading = /^#\s+(.*)$/m.exec(body)?.[1];
    return {
      slug,
      title: asString(data.title, firstHeading ?? toTitle(slug)),
      category: asString(data.category, DEFAULT_CATEGORY),
      personaTags: asList(data.persona_tags),
      source: body,
      sections: extractSections(body),
    };
  })
  .sort(
    (left, right) =>
      left.category.localeCompare(right.category) || left.title.localeCompare(right.title)
  );

/**
 * A doc with no persona tags is "all roles" and is visible to everyone. An
 * UNKNOWN persona (`null` — a session whose claims could not be read) is
 * treated as least-privilege: untagged pages only, never the coaching material.
 */
export function isDocVisibleTo(page: DocPage, persona: Persona | null): boolean {
  if (page.personaTags.length === 0) return true;
  return persona !== null && page.personaTags.includes(persona);
}

/**
 * ToC scope: the caller's persona, or every doc when `isAllRoles` is set.
 * A Fanatic therefore sees Fanatic-tagged and untagged docs only — never the
 * coaching material.
 */
export function docsForPersona(persona: Persona | null, isAllRoles = false): DocPage[] {
  return DOC_PAGES.filter((page) => isAllRoles || isDocVisibleTo(page, persona));
}

/** Groups a doc list into ToC categories, preserving the sorted order. */
export function groupDocsByCategory(pages: readonly DocPage[]): [string, DocPage[]][] {
  const groups = new Map<string, DocPage[]>();
  for (const page of pages) {
    const bucket = groups.get(page.category);
    if (bucket === undefined) groups.set(page.category, [page]);
    else bucket.push(page);
  }
  return [...groups.entries()];
}

export function findDoc(slug: string | undefined): DocPage | undefined {
  return DOC_PAGES.find((page) => page.slug === slug);
}
